/**
 * Tiny first-party tracker. Queues events, flushes in batches, and uses
 * sendBeacon when the page is hidden or unloading.
 */
import type { Router } from 'vue-router'
import { supabase } from '@/supabase'
import { GUEST_MODE } from '@/guest/guestMode'
import { randomId } from '@/utils/randomId'
import {
    alreadyStitched,
    currentDevice,
    ensureIdentity,
    readAnonId,
    isLandingPath,
    isPublicTrackingPath,
    markStitchPending,
    markStitched,
    readStitchPending,
    shouldStitchIdentity,
    type Touch,
} from '@/analytics/identity'

const FLUSH_MS = 2000
const MAX_BATCH = 20
const SENSITIVE = /password|email|token|authorization|cookie|secret/i

interface QueuedEvent {
    event_id: string
    event: string
    properties: Record<string, unknown>
    path: string
    occurred_at: string
}

let queue: QueuedEvent[] = []
let timer: ReturnType<typeof setTimeout> | null = null
let installed = false
let landingArrivedAt = 0

function endpoint(): string {
    const base = (import.meta.env.VITE_APP_BACKEND_URL || '').replace(/\/$/, '')
    return base ? `${base}/t` : ''
}

function sanitize(properties: Record<string, unknown>): Record<string, unknown> {
    const cleaned: Record<string, unknown> = {}
    for (const [key, value] of Object.entries(properties)) {
        if (!key || SENSITIVE.test(key)) continue
        if (typeof value === 'string') cleaned[key] = value.slice(0, 300)
        else if (typeof value === 'number' && Number.isFinite(value)) cleaned[key] = value
        else if (typeof value === 'boolean' || value === null) cleaned[key] = value
        if (Object.keys(cleaned).length >= 20) break
    }
    return cleaned
}

function payload(events: QueuedEvent[]) {
    const identity = ensureIdentity()
    return {
        anon_id: identity.anonId,
        session_id: identity.sessionId,
        device: currentDevice(),
        first_touch: identity.first as Touch,
        last_touch: identity.last as Touch,
        events,
    }
}

export function landingElapsedMs(): number {
    if (!landingArrivedAt || typeof performance === 'undefined') return 0
    return Math.max(0, Math.round(performance.now() - landingArrivedAt))
}

export function markLandingArrival() {
    if (typeof performance === 'undefined') return
    landingArrivedAt = performance.now()
}

/** Framed pages are previews (e.g. a landing page shown inside /keynote), not visits. */
function isFramed(): boolean {
    try {
        return window.self !== window.top
    }
    catch {
        return true
    }
}

export function track(event: string, properties: Record<string, unknown> = {}) {
    if (GUEST_MODE || typeof window === 'undefined' || isFramed()) return
    if (!/^[a-z][a-z0-9_]{0,63}$/.test(event)) return
    ensureIdentity()
    queue.push({
        event_id: randomId(),
        event,
        properties: sanitize(properties),
        path: window.location.pathname,
        occurred_at: new Date().toISOString(),
    })
    if (queue.length >= MAX_BATCH) void flush(false)
    else schedule()
}

function schedule() {
    if (timer != null) return
    timer = setTimeout(() => {
        timer = null
        void flush(false)
    }, FLUSH_MS)
}

export async function flush(useBeacon: boolean): Promise<void> {
    if (timer != null) {
        clearTimeout(timer)
        timer = null
    }
    if (!queue.length || typeof window === 'undefined') return
    const events = queue
    queue = []
    const url = endpoint()
    if (!url) return
    const body = JSON.stringify(payload(events))
    if (useBeacon && typeof navigator.sendBeacon === 'function') {
        const blob = new Blob([body], { type: 'text/plain;charset=UTF-8' })
        const ok = navigator.sendBeacon(url, blob)
        if (!ok) queue = events.concat(queue)
        return
    }
    try {
        await fetch(url, {
            method: 'POST',
            body,
            headers: { 'Content-Type': 'application/json' },
            keepalive: true,
            credentials: 'omit',
            mode: 'cors',
        })
    }
    catch {
        // The next interaction flushes a new batch. Don't retry in a loop.
    }
}

export function installPublicPageTracking(router: Router) {
    if (installed || GUEST_MODE || typeof window === 'undefined') return
    installed = true
    window.addEventListener('pagehide', () => {
        void flush(true)
    })
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') void flush(true)
    })
    router.afterEach((to) => {
        if (!isPublicTrackingPath(to.path)) return
        if (isLandingPath(to.path)) markLandingArrival()
        track('page_viewed', { route: String(to.name ?? '') })
    })
}

async function postIdentify(accessToken: string): Promise<boolean> {
    const url = endpoint()
    if (!url) return false
    const identity = ensureIdentity()
    if (alreadyStitched(identity.anonId)) return true
    const response = await fetch(`${url}/identify`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
            anon_id: identity.anonId,
            session_id: identity.sessionId,
        }),
    })
    if (response.ok) {
        markStitched(identity.anonId)
        return true
    }
    return false
}

/** Call after a successful signup. Retries once if the user row is not visible yet. */
export async function requestIdentityStitch(createdAt?: string | null) {
    if (GUEST_MODE || typeof window === 'undefined') return
    const existing = readAnonId()
    if (existing && alreadyStitched(existing)) return
    markStitchPending()
    const { data } = await supabase.auth.getSession()
    const token = data.session?.access_token
    const created = createdAt || data.session?.user?.created_at
    if (!token || !shouldStitchIdentity(created, readStitchPending())) return
    await flush(false)
    const ok = await postIdentify(token)
    if (!ok) {
        await new Promise((resolve) => setTimeout(resolve, 600))
        await postIdentify(token)
    }
}

/** SIGNED_IN / INITIAL_SESSION. No-ops for returning accounts. */
export async function maybeStitchIdentity(createdAt: string | null | undefined) {
    if (GUEST_MODE || typeof window === 'undefined') return
    const existing = readAnonId()
    if (existing && alreadyStitched(existing)) return
    if (!shouldStitchIdentity(createdAt, readStitchPending())) return
    const { data } = await supabase.auth.getSession()
    const token = data.session?.access_token
    if (!token) return
    await flush(false)
    await postIdentify(token)
}
